import React, { useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  IconButton,
  useColorModeValue,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Menu as MenuIcon } from "lucide-react";
import { useMessages } from "../store/messages-context";
import { useTranslation } from "react-i18next";
import { GET_STARTED_BACK_EVENT } from "../constants/mobileApp";

export const SETTINGS_MENU_EVENT = "payinapo:open-settings-menu";

export const MOBILE_TOP_BAR_HEIGHT = 54;

function getScreenInfo(pathname, t) {
  const roots = {
    "/shop": t("nav.explore"),
    "/likes": t("nav.likes"),
    "/Messages": t("nav.chats"),
    "/settings": t("nav.settings"),
  };
  if (roots[pathname]) {
    return { title: roots[pathname], showBack: false };
  }
  if (pathname.startsWith("/messages/")) return { title: t("nav.chat"), showBack: true };
  if (pathname.startsWith("/profile/")) return { title: t("nav.profile"), showBack: true };
  if (pathname.startsWith("/match/")) return { title: t("nav.match"), showBack: true };
  if (pathname.startsWith("/like/")) return { title: t("nav.like"), showBack: true };
  if (pathname === "/onboarding") return { title: t("nav.getStartedTitle"), showBack: true };
  if (pathname === "/get-started") return { title: t("nav.welcome"), showBack: true };
  return { title: t("nav.payinapo"), showBack: false };
}

export default function MobileTopBar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { totalUnreadCount } = useMessages();

  const { title, showBack } = useMemo(
    () => getScreenInfo(location.pathname, t),
    [location.pathname, t]
  );

  let hasUser = false;
  try {
    hasUser = !!localStorage.getItem("user");
  } catch {
    hasUser = false;
  }

  const isAppShell =
    hasUser &&
    location.pathname !== "/" &&
    location.pathname !== "/Account-disabled" &&
    !location.pathname.startsWith("/admin");

  const barBg = useColorModeValue("rgba(255,255,255,0.85)", "rgba(14,14,16,0.82)");
  const barBorder = useColorModeValue("rgba(15,23,42,0.08)", "rgba(255,255,255,0.06)");
  const titleColor = useColorModeValue("gray.900", "white");
  const iconColor = useColorModeValue("gray.700", "whiteAlpha.800");
  const iconHover = useColorModeValue("gray.100", "whiteAlpha.100");

  if (!isAppShell) return null;

  // Conversation view uses its own in-chat header (avatar, name, back).
  if (location.pathname.startsWith("/messages/")) return null;

  const showSettingsMenu = location.pathname === "/settings";
  const showChatsTabBadge =
    totalUnreadCount > 0 &&
    (location.pathname === "/Messages" || location.pathname.startsWith("/messages/"));

  return (
    <Box
      display={{ base: "block", lg: "none" }}
      position="sticky"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      sx={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      <Box
        bg={barBg}
        backdropFilter="blur(16px) saturate(160%)"
        borderBottomWidth="1px"
        borderBottomColor={barBorder}
      >
        <Flex
          align="center"
          justify="space-between"
          h={`${MOBILE_TOP_BAR_HEIGHT}px`}
          px={3}
          gap={2}
        >
        <Flex align="center" w="44px" justify="flex-start">
          {showBack && (
            <IconButton
              aria-label={t("common.back")}
              icon={<ArrowLeft size={20} />}
              variant="ghost"
              size="sm"
              h="40px"
              minW="40px"
              w="40px"
              borderRadius="full"
              color={iconColor}
              _hover={{ bg: iconHover }}
              _active={{ transform: "scale(0.95)" }}
              onClick={() => {
                if (location.pathname === "/get-started") {
                  window.dispatchEvent(new CustomEvent(GET_STARTED_BACK_EVENT));
                  return;
                }
                navigate(-1);
              }}
            />
          )}
          {!showBack && showSettingsMenu && (
            <IconButton
              aria-label={t("nav.openSettingsMenu")}
              icon={<MenuIcon size={20} />}
              variant="ghost"
              size="sm"
              h="40px"
              minW="40px"
              w="40px"
              borderRadius="full"
              color={iconColor}
              _hover={{ bg: iconHover }}
              _active={{ transform: "scale(0.95)" }}
              onClick={() =>
                window.dispatchEvent(new CustomEvent(SETTINGS_MENU_EVENT))
              }
            />
          )}
        </Flex>

        <Flex flex="1" justify="center" align="center" gap={2} minW={0} px={1}>
          <Text
            textAlign="center"
            fontSize="md"
            fontWeight="semibold"
            color={titleColor}
            letterSpacing="-0.01em"
            noOfLines={1}
          >
            {title}
          </Text>
          {showChatsTabBadge && (
            <Flex
              minW="20px"
              h="20px"
              px="6px"
              align="center"
              justify="center"
              borderRadius="full"
              flexShrink={0}
              bg="rgba(254, 226, 226, 0.95)"
              color="#b91c1c"
              fontSize="11px"
              fontWeight="bold"
              lineHeight={1}
              borderWidth="1px"
              borderColor="rgba(252, 165, 165, 0.7)"
              boxShadow="0 2px 8px rgba(239, 68, 68, 0.22)"
            >
              {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
            </Flex>
          )}
        </Flex>

        <Flex align="center" w="44px" justify="flex-end" />
      </Flex>
      </Box>
    </Box>
  );
}
